import React, { useState, useRef, useEffect } from "react";

import {
  IconChevronDown,
  IconChevronUp,
  IconSearch,
  IconXMark,
} from "@frontend/common";

import PressLabelViewer from "@/components/shared/ui/PressLabelViewer/PressLabelViewer";
import { usePressInfoQuery } from "@/queries/hooks/usePressInfo";
import { ClassificationCode } from "@/types/code.types";
import { getArticleInfoData } from "@api/articleInfo/useArticleInfo";
import useDebounce from "@FillInTableContainer/shared/hooks/useDebounce";
import { getPressIdFromURL } from "@utils/getStateFromURL";
import { parseJSON } from "@utils/parseJSON";

import * as S from "./FillInTablePress.style";
import { removeLineBreaksCopyText } from "./utils/removeLineBreaksCopyText/removeLineBreaksCopyText";

import type { ParsedJsonContentType } from "@/types/fillInTableContent.types";

interface Props {
  columnLabelViewerSize?: {
    width: number;
    y: number;
  };
}

const FillInTablePress = ({ columnLabelViewerSize }: Props) => {
  const draftArticleId = Number(getPressIdFromURL());
  const { data: pressInfo } = usePressInfoQuery(draftArticleId);
  const articleInfo = getArticleInfoData(draftArticleId);
  const classificationManual =
    articleInfo?.data.classificationManual ||
    (articleInfo?.data.topicType as ClassificationCode) ||
    "";
  const isCTG = articleInfo?.data.classificationManual === "ctg";

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResultsCount, setSearchResultsCount] = useState(0);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(0);
  const [focusedStartIndex, setFocusedStartIndex] = useState<number | null>(
    null,
  );
  const [searchNodes, setSearchNodes] = useState<React.ReactElement[]>([]);
  const [targetNode, setTargetNode] = useState<React.ReactElement | null>(null);
  const [focusInnerNodes, setFocusInnerNodes] = useState<React.ReactNode[]>([]);
  const viewerRef = useRef<HTMLDivElement>(null);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  useEffect(() => {
    const searchNodesArray: React.ReactNode[] = [];

    // 1----->서치로 들어오는 애들 추출
    focusInnerNodes.forEach((node) => {
      if (Array.isArray(node)) {
        node.forEach((nodeItem) => {
          if (nodeItem?.key?.includes("search-")) {
            searchNodesArray.push(nodeItem);
          }
        });
      }
    });

    // 2----->인접한 검색 결과를 그룹화
    const groupedSearchNodes: React.ReactNode[][] = [];
    let currentGroup: React.ReactNode[] = [];

    searchNodesArray.forEach((node) => {
      currentGroup.push(node);
      groupedSearchNodes.push([...currentGroup]);
      currentGroup = [];
    });

    if (currentGroup.length > 0) {
      groupedSearchNodes.push([...currentGroup]);
    }

    const firstElements = groupedSearchNodes.map((group) => group[0]);

    const adjacentElements = firstElements
      .map((element, index) => {
        const nextElement = firstElements[index + 1];
        if (!React.isValidElement(nextElement)) return element;
        if (element?.props?.item?.type === "image") return undefined;
        if (
          nextElement?.props["data-start-index"] !==
          element?.props["data-end-index"]
        ) {
          return element;
        }
        return undefined;
      })
      .filter(
        (element): element is React.ReactElement => element !== undefined,
      );

    const focusIndices = adjacentElements.map(
      (node) => node.props["data-start-index"],
    );

    setSearchResultsCount(focusIndices.length);
    setSearchNodes(adjacentElements);
  }, [searchTerm, focusInnerNodes]);

  if (!pressInfo) return null;
  const rawPressContent = pressInfo.pressContent;
  const pressContent: ParsedJsonContentType[] = parseJSON(rawPressContent);

  const getAdditionalBoldKeywords = () => {
    if (isCTG) {
      return [...(articleInfo?.data.sponsorName || [])];
    }

    return articleInfo?.data?.companyName ? [articleInfo.data.companyName] : [];
  };

  const additionalBoldKeywords = getAdditionalBoldKeywords();

  const handleSearchNavigation = (direction: "up" | "down") => {
    if (searchResultsCount === 0) return;

    let newIndex;
    if (direction === "up") {
      newIndex =
        currentSearchIndex > 0
          ? currentSearchIndex - 1
          : searchResultsCount - 1;
    } else {
      newIndex =
        currentSearchIndex < searchResultsCount - 1
          ? currentSearchIndex + 1
          : 0;
    }
    setCurrentSearchIndex(newIndex);

    const newTargetNode = searchNodes[newIndex];
    setTargetNode(newTargetNode);

    if (newTargetNode) {
      const startIndex = newTargetNode.props["data-start-index"];
      const tableKey = newTargetNode.props["data-table-key"];

      const tableRowIndex = newTargetNode.props["data-table-row-index"];
      setFocusedStartIndex(startIndex);

      setTimeout(() => {
        const container = viewerRef.current;
        if (container) {
          let element = null;
          const isTable = newTargetNode.props["data-table-key"];

          if (isTable) {
            element = container.querySelector(
              `[data-start-index="${startIndex}"][data-table-key="${tableKey}"][data-table-row-index="${tableRowIndex}"]`,
            );
          } else {
            element = container.querySelector(
              `[data-start-index="${startIndex}"]`,
            );
          }

          if (element) {
            const elementRect = element.getBoundingClientRect();
            const containerRect = container.getBoundingClientRect();

            const elementCenter = elementRect.top + elementRect.height / 2;
            const containerCenter =
              containerRect.top + containerRect.height / 2;

            const scrollPosition =
              container.scrollTop + (elementCenter - containerCenter);

            container.scrollTo({
              top: scrollPosition,
              behavior: "smooth",
            });
          }
        }
      }, 0);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchTerm = e.target.value;
    setSearchTerm(newSearchTerm);
    setCurrentSearchIndex(-1);
    setFocusedStartIndex(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearchNavigation("down");
    }
  };

  return (
    <S.PressLabelViewerContainerWrapper
      onKeyDown={(e) => {
        if (e.key === "Enter" && searchTerm) {
          handleSearchNavigation("down");
        }
      }}
      tabIndex={0}
    >
      <S.SearchInputWrapper searchTerm={!!searchTerm}>
        <S.SearchIcon>
          <IconSearch width={24} height={24} color="#A8ADB5" />
        </S.SearchIcon>
        <S.SearchInputContainer
          type="text"
          placeholder="Search Keywords"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyDown={handleKeyDown}
          searchTerm={!!searchTerm}
        />
        {searchTerm && (
          <S.SearchControls>
            <S.NumberText>
              {searchResultsCount !== 0 &&
                `${currentSearchIndex + 1}/${searchResultsCount}`}
              {searchResultsCount === 0 && `0/0`}
            </S.NumberText>
            <S.UpIcon onClick={() => handleSearchNavigation("up")}>
              <IconChevronUp width={28} height={28} color="#515C6C" />
            </S.UpIcon>
            <S.DownIcon onClick={() => handleSearchNavigation("down")}>
              <IconChevronDown width={28} height={28} color="#515C6C" />
            </S.DownIcon>
            <S.DeleteIcon
              onClick={() => {
                setSearchTerm("");
                setCurrentSearchIndex(0);
                setSearchResultsCount(0);
              }}
            >
              <IconXMark width={22} height={22} color="#515C6C" />
            </S.DeleteIcon>
          </S.SearchControls>
        )}
      </S.SearchInputWrapper>

      <S.PressLabelViewerContainer
        onCopy={removeLineBreaksCopyText}
        ref={viewerRef}
      >
        <S.ViewerWrapper
          $start={targetNode?.props["data-start-index"]}
          $target={targetNode?.props["data-table-row-index"]}
        >
          <PressLabelViewer
            parsedContentArray={pressContent}
            draftArticleId={draftArticleId}
            columnLabelViewerWidth={columnLabelViewerSize?.width}
            columnLabelViewerTop={columnLabelViewerSize?.y}
            classificationManual={classificationManual}
            additionalBoldKeywords={additionalBoldKeywords}
            searchTerm={debouncedSearchTerm}
            currentSearchIndex={currentSearchIndex}
            focusedStartIndex={focusedStartIndex}
            onSearchResultsCount={setSearchResultsCount}
            focusInnerNodes={focusInnerNodes}
            onFocusInnerNodesChange={(nodes) => {
              setFocusInnerNodes(nodes);
            }}
          />
        </S.ViewerWrapper>
      </S.PressLabelViewerContainer>
    </S.PressLabelViewerContainerWrapper>
  );
};

export default FillInTablePress;
